import React from "react";
import Select from "react-select";
import { Button } from "../../../components/common/Button";
import { Input } from "../../../components/common/Input";
import { ClientSortBy, DealStatusCatalog, DealStatusId } from "../types";
import { SEARCH_PLACEHOLDER } from "../constants";
import "./styles.css";

type SelectOption = {
    value: string;
    label: string;
};

type Props = {
    search: string;
    statuses: DealStatusId[];
    sortBy: ClientSortBy;
    periodFrom: string;
    periodTo: string;
    catalog: DealStatusCatalog;
    isAuth: boolean;
    onChangeSearch: (value: string) => void;
    onChangeStatuses: (values: DealStatusId[]) => void;
    onChangeSortBy: (value: ClientSortBy) => void;
    onChangePeriodFrom: (value: string) => void;
    onChangePeriodTo: (value: string) => void;
    onOpenAddModal: () => void;
};

const SORT_OPTIONS: SelectOption[] = [
    { value: "fullname", label: "По ФИО" },
    { value: "createdAt", label: "Сначала новые" },
    { value: "inProgressDealsCount", label: "Больше сделок в работе" },
    { value: "completedDealsCount", label: "Больше завершённых сделок" },
    {
        value: "completedDealsPropertyPriceSum",
        label: "По объёму завершённых сделок",
    },
];

export const FiltersBar: React.FC<Props> = ({
    search,
    statuses,
    sortBy,
    periodFrom,
    periodTo,
    catalog,
    isAuth,
    onChangeSearch,
    onChangeStatuses,
    onChangeSortBy,
    onChangePeriodFrom,
    onChangePeriodTo,
    onOpenAddModal,
}) => {
    const isMulti = statuses.length > 1;
    const statusOptions: SelectOption[] = [
        { value: "all", label: "Все статусы" },
        ...catalog.map((status) => ({
            value: String(status.code),
            label: status.name,
        })),
        ...(isMulti
            ? [{ value: "multi", label: `Выбрано ${statuses.length}` }]
            : []),
    ];
    const statusValue = statuses.length === 0
        ? "all"
        : isMulti
            ? "multi"
            : String(statuses[0]);
    const selectedStatus =
        statusOptions.find((option) => option.value === statusValue) ??
        statusOptions[0];
    const selectedSort =
        SORT_OPTIONS.find((option) => option.value === sortBy) ??
        SORT_OPTIONS[0];
    const periodError =
        periodFrom && periodTo && periodFrom > periodTo
            ? "Дата начала не может быть позже даты окончания"
            : "";

    const handleStatusChange = (option: SelectOption | null) => {
        if (!option || option.value === "multi") return;

        onChangeStatuses(
            option.value === "all"
                ? []
                : [option.value as DealStatusId],
        );
    };

    return (
        <div className="client-filters">
            <div className="client-filters__search">
                <Input
                    label="Поиск"
                    name="clientSearch"
                    type="search"
                    placeholder={SEARCH_PLACEHOLDER}
                    value={search}
                    onChange={onChangeSearch}
                    labelClassName="client-filters__label"
                />
            </div>

            <div className="client-filters__field client-filters__status">
                <div className="client-filters__label">Статус</div>
                <Select<SelectOption, false>
                    value={selectedStatus}
                    options={statusOptions}
                    onChange={handleStatusChange}
                    isSearchable={false}
                    className="client-filters__select"
                    classNamePrefix="rf"
                    aria-label="Статус сделки"
                    menuPlacement="auto"
                />
            </div>

            <div className="client-filters__field client-filters__sorting">
                <div className="client-filters__label">Сортировка</div>
                <Select<SelectOption, false>
                    value={selectedSort}
                    options={SORT_OPTIONS}
                    onChange={(option) => {
                        if (option) {
                            onChangeSortBy(option.value as ClientSortBy);
                        }
                    }}
                    isSearchable={false}
                    className="client-filters__select"
                    classNamePrefix="rf"
                    aria-label="Сортировка клиентов"
                    menuPlacement="auto"
                />
            </div>

            <div className="client-filters__period">
                <div
                    className="client-filters__period-fields"
                    role="group"
                    aria-label="Период сделок"
                >
                    <div className="client-filters__period-field">
                        <Input
                            label="Сделки с"
                            labelClassName="client-filters__label"
                            name="periodFrom"
                            type="date"
                            value={periodFrom}
                            max={periodTo || undefined}
                            onChange={onChangePeriodFrom}
                            error={Boolean(periodError)}
                        />
                    </div>
                    <div className="client-filters__period-field">
                        <Input
                            label="По"
                            labelClassName="client-filters__label"
                            name="periodTo"
                            type="date"
                            value={periodTo}
                            min={periodFrom || undefined}
                            onChange={onChangePeriodTo}
                            error={Boolean(periodError)}
                        />
                    </div>
                </div>
                {periodError && (
                    <div className="client-filters__period-error" role="alert">
                        {periodError}
                    </div>
                )}
            </div>

            <Button
                text="Добавить клиента"
                onClick={onOpenAddModal}
                disabled={!isAuth}
                pending={false}
                variant="primary"
                className="client-filters__add"
            />
        </div>
    );
};
